import { useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash.throttle';

/**
 * Wraps a given callback in a cancelable, throttled function. The throttled
 * callback is stable and will never change. It will be automatically cancelled
 * on unmount, unless the `flushOnUnmount` option is passed in, then it will be flushed on unmount.
 * At the time the throttled function is called, it will call the latest callback that has been passed in.
 * @param callback callback function to call with throttling
 * @param throttleMs throttle milliseconds
 * @param initialOptions lodash throttle options. Will not react to changes to this param
 * @returns a cancelable, throttled function
 */
export function useThrottledCallback(callback, throttleMs, initialOptions) {
  var options = useRef(initialOptions);

  // Use a ref for the callback
  // We want to keep a stable callback so the flush/cancel works as expected
  // So we keep a ref to the current callback, then we have a throttled callback that will just call this
  var callbackRef = useRef(callback);
  callbackRef.current = callback;
  var throttledCallback = useMemo(() => throttle(function () {
    return callbackRef.current(...arguments);
  }, throttleMs, options.current), [throttleMs]);
  useEffect(() => () => {
    var _options$current$flus, _options$current;
    if ((_options$current$flus = (_options$current = options.current) === null || _options$current === void 0 ? void 0 : _options$current.flushOnUnmount) !== null && _options$current$flus !== void 0 ? _options$current$flus : false) {
      throttledCallback.flush();
    } else {
      throttledCallback.cancel();
    }
  }, [throttledCallback]);
  return throttledCallback;
}
export default useThrottledCallback;
//# sourceMappingURL=useThrottledCallback.js.map